/**
 * MyTimeBilling: My time's case-note list as the pre-billing view of the
 * viewer's own notes (GET /v1/my-prebilling). Every case note she wrote
 * in the pay period, across her clients, newest first, twenty to a page,
 * each drawn by the shared Pre-billing row: the client, the band, the
 * hours ("1.5 h"), the invoice line, the Not ready | Ready switch with its
 * state words beside it, and Edit in its own column.
 *
 *   - Hours only, never a rate or an amount, for every seat.
 *   - Edit opens the whole line on her own note, as on the client's
 *     Pre-billing: date, hours, the rate bands she may pick (labels, no
 *     price), time of day and the invoice line, with one Save.
 *   - A note on a client her scope no longer reaches stays listed: the
 *     name is plain text and "Not on your care team now" sits under it,
 *     and the note is still hers: she marks it Ready and edits its line.
 *   - `fixNoteId` (the URL's fix_note, Your day's door for a kickback on a
 *     client she can no longer open) opens that row's line, on its page.
 *   - "Mark my notes ready (n)" sits above the list, on the right.
 *   - From `sm` up the notes are a table with fixed columns; below it each
 *     note is one card holding the same fields.
 *
 * A mark or a line save refreshes this list, My time's hours and the
 * client's Pre-billing.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/primitives/Button';
import { PaginationFooter } from '@/components/primitives/PaginationFooter';
import { ConfirmDialog } from '@/components/workspace/ConfirmDialog';
import {
  CombinedSaveError,
  EDIT_COLUMN_WIDTH,
  saveBillingLine,
  OWN_NOTES_STATUS_COLUMN_WIDTH,
  PrebillingRow,
  columnHeading,
  editProblemFor,
  readyRefusalMessage,
  type EditProblem,
} from '@/components/clientRecord/PreBilling';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useSupportContact } from '@/hooks/useSupportContact';
import { useTenant } from '@/lib/auth';
import { canChangeNote, canWriteClinicalNotes } from '@/lib/capabilities';
import { invalidateInvoiceReads } from '@/lib/invoiceInvalidation';
import { invalidateMyPrebilling, useMyPrebilling } from '@/lib/myPrebilling';
import { patchNoteSummary, setNoteBillingReady, type BillingPatch } from '@/lib/prebilling';
import { bandOptionsForWhoami } from '@/lib/bandVisibility';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { notesApi } from '@/lib/notes';
import { removeErrorMessage } from '@/components/notes/NotesFeed';
import type { RateBandOption } from '@/lib/notes';
import { invalidateMyHours } from '@/lib/timeEntriesApi';

/** The bulk mark's confirm (LP-27). */
export function markReadyTitle(count: number): string {
  return `Mark ${count} ${count === 1 ? 'note' : 'notes'} ready?`;
}
export const MARK_READY_BODY = 'Each one goes to billing as it is. You can still edit a note until the period closes.';

/** Notes per page. */
const PAGE_SIZE = 20;

/** Below `sm` (640px) each note is a card. */
const CARD_MAX_WIDTH_PX = 639;

/** The table's columns, fixed so a mark never re-flows them. The invoice
 *  line takes what the others leave, and the table's minimum width keeps
 *  that at least 24rem: below it the table scrolls sideways
 *  in its card instead of squeezing the line onto three or more lines. */
const COLUMNS: ReadonlyArray<{ name: string; width: string | undefined }> = [
  { name: 'Date', width: 'w-[9rem]' },
  { name: 'Client', width: 'w-[12rem]' },
  { name: 'Hours', width: 'w-[5rem]' },
  { name: 'Invoice line', width: undefined },
  { name: 'Status', width: OWN_NOTES_STATUS_COLUMN_WIDTH },
  { name: 'Edit', width: EDIT_COLUMN_WIDTH },
];

export interface MyTimeBillingProps {
  tenantSlug: string;
  /** The period in the URL (YYYY-MM-DD), or null for today's. */
  periodStart: string | null;
  /** The tenant's rate bands, for the band's words. */
  bands: readonly RateBandOption[];
  /** A note whose line opens for editing once its row is listed. */
  fixNoteId?: string | null;
}

export function MyTimeBilling({ tenantSlug, periodStart, bands, fixNoteId = null }: MyTimeBillingProps): ReactNode {
  const { whoami } = useTenant();
  const contact = useSupportContact();
  const queryClient = useQueryClient();
  const canWrite = canWriteClinicalNotes(whoami);
  const asCards = useIsMobile(CARD_MAX_WIDTH_PX);
  const { data, isLoading, isError } = useMyPrebilling(tenantSlug, periodStart);
  const rows = useMemo(() => data?.rows ?? [], [data]);
  const bandLabels = useMemo(() => Object.fromEntries(bands.map((b) => [b.value, b.label])), [bands]);
  const bandLabel = (band: string | null): string => (band === null ? 'No band' : (bandLabels[band] ?? band));

  // After a mark or a save, this list, My time's hours and the client's
  // Pre-billing all read again.
  const refresh = useCallback(async (): Promise<void> => {
    invalidateInvoiceReads(queryClient, tenantSlug);
    void invalidateMyHours(queryClient, tenantSlug);
    void queryClient.invalidateQueries({ queryKey: ['prebilling', tenantSlug] });
    await invalidateMyPrebilling(queryClient, tenantSlug);
  }, [queryClient, tenantSlug]);

  // The Ready switch: the chosen value shows at once and holds until the
  // refetch has landed, so the switch never flickers back.
  const [pendingReady, setPendingReady] = useState<Record<string, boolean>>({});
  const [readyProblem, setReadyProblem] = useState<{ noteId: string; message: string } | null>(null);
  const readyMut = useMutation<unknown, unknown, { noteId: string; ready: boolean }>({
    mutationFn: ({ noteId, ready }) => setNoteBillingReady(tenantSlug, noteId, ready),
    onMutate: ({ noteId, ready }) => {
      setReadyProblem(null);
      setPendingReady((p) => ({ ...p, [noteId]: ready }));
    },
    onSuccess: refresh,
    onSettled: (_res, _err, { noteId }) => {
      setPendingReady((p) => {
        const next = { ...p };
        delete next[noteId];
        return next;
      });
    },
    onError: (err, { noteId }) => {
      // My time shows no money for any seat, so every seat reads the
      // care manager's refusals.
      setReadyProblem({ noteId, message: readyRefusalMessage(err, false, contact) });
    },
  });

  // Edit: the invoice line alone, one box at a time.
  const [editingRow, setEditingRow] = useState<string | null>(null);
  const [editProblem, setEditProblem] = useState<({ noteId: string } & EditProblem) | null>(null);
  const returnFocusTo = useRef<string | null>(null);
  const onFocusReturned = useCallback((): void => {
    returnFocusTo.current = null;
  }, []);
  // The whole line (her own note): the shared Save, line then billing.
  // The bands offered are the ones she may pick (band visibility), by
  // label; My time shows no money to any seat.
  const { data: tenantConfig } = useTenantConfig(tenantSlug);
  const allowedBands = useMemo(
    () => bandOptionsForWhoami(whoami, tenantConfig?.config, bands),
    [whoami, tenantConfig, bands],
  );
  const billingMut = useMutation<unknown, unknown, { noteId: string; patch: BillingPatch; summary?: string }>({
    mutationFn: ({ noteId, patch, summary }) => saveBillingLine(tenantSlug, noteId, patch, summary),
    onMutate: () => setEditProblem(null),
    onSuccess: (_res, { noteId }) => {
      returnFocusTo.current = noteId;
      setEditingRow(null);
      void refresh();
    },
    onError: (err, { noteId }) => {
      setEditProblem({ noteId, ...editProblemFor(err, contact) });
      // The line half landed: read again so the row tells the truth.
      if (err instanceof CombinedSaveError && err.lineSaved) void refresh();
    },
  });
  const lineMut = useMutation<unknown, unknown, { noteId: string; summary: string }>({
    mutationFn: async ({ noteId, summary }) => {
      try {
        await patchNoteSummary(tenantSlug, noteId, summary);
      } catch (err) {
        throw new CombinedSaveError('line', false, err);
      }
      return null;
    },
    onMutate: () => setEditProblem(null),
    onSuccess: (_res, { noteId }) => {
      returnFocusTo.current = noteId;
      setEditingRow(null);
      void refresh();
    },
    onError: (err, { noteId }) => setEditProblem({ noteId, ...editProblemFor(err, contact) }),
  });

  // Remove note: the Notes tab's remove, after the box's confirm, so a
  // note logged on a practice client comes off from here too.
  // The list, My time's hours, the client's Pre-billing and Notes feed and
  // the invoice pages read again; a refusal says the server's sentence.
  const [removeProblem, setRemoveProblem] = useState<{ noteId: string; message: string } | null>(null);
  const removeMut = useMutation<unknown, unknown, { noteId: string }>({
    mutationFn: ({ noteId }) => notesApi.removeNote(tenantSlug, noteId),
    onMutate: () => setRemoveProblem(null),
    onSuccess: () => {
      setEditingRow(null);
      setEditProblem(null);
      void queryClient.invalidateQueries({ queryKey: ['kith-notes', tenantSlug] });
      void refresh();
    },
    onError: (err, { noteId }) => setRemoveProblem({ noteId, message: removeErrorMessage(err, contact) }),
  });

  // Every row here is the viewer's own note. She marks it and edits its
  // line with write_clinical_notes, whether or not her scope still reaches
  // the client: a note's billing stays its author's to fix.
  const isOwn = (authorUserId: string): boolean => whoami !== null && authorUserId === whoami.user_id;
  const awaiting = rows.filter(
    (r) => r.note.readiness === 'needs_your_ok' && canWrite && isOwn(r.note.authorUserId) && pendingReady[r.note.noteId] !== true,
  );
  // Once the bulk button has shown for a period it stays, at (0) and
  // disabled, when nothing is left, so the list never moves.
  const periodKey = data?.period.start ?? '';
  const [bulkShownFor, setBulkShownFor] = useState<string | null>(null);
  useEffect(() => {
    if (canWrite && awaiting.length > 0 && bulkShownFor !== periodKey) setBulkShownFor(periodKey);
  }, [canWrite, awaiting.length, bulkShownFor, periodKey]);
  const showBulk = canWrite && (awaiting.length > 0 || bulkShownFor === periodKey);
  // "Mark my notes ready" asks first (LP-27): the confirm names the notes
  // counted when it opened, and nothing is sent until its primary.
  const [confirmMarking, setConfirmMarking] = useState<readonly string[] | null>(null);

  // Twenty to a page; a new period or a different set of notes starts
  // again on page 1, a mark or an edit keeps the page.
  const pagerKey = `${periodKey}|${rows.map((r) => r.note.noteId).join(',')}`;
  const [pager, setPager] = useState<{ key: string; offset: number }>({ key: '', offset: 0 });
  if (pager.key !== pagerKey) {
    // A note to fix starts on its own page.
    const at = fixNoteId === null ? -1 : rows.findIndex((r) => r.note.noteId === fixNoteId);
    setPager({ key: pagerKey, offset: at < 0 ? 0 : Math.floor(at / PAGE_SIZE) * PAGE_SIZE });
  }
  const offset = pager.key === pagerKey ? pager.offset : 0;
  const pageRows = rows.slice(offset, offset + PAGE_SIZE);

  // fix_note opens its row's line once, when the row is listed and hers
  // to edit.
  const fixOpened = useRef<string | null>(null);
  const fixRowListed = fixNoteId !== null && pageRows.some((r) => r.note.noteId === fixNoteId);
  useEffect(() => {
    if (!canWrite || fixNoteId === null || !fixRowListed || fixOpened.current === fixNoteId) return;
    fixOpened.current = fixNoteId;
    setEditingRow(fixNoteId);
  }, [canWrite, fixNoteId, fixRowListed]);

  if (isLoading && data === null) {
    return <p className="text-sm text-ink-soft">Loading your case notes…</p>;
  }
  if (isError && data === null) {
    return <p className="text-sm text-ink-soft">Your case notes did not load. Reload the page to try again.</p>;
  }
  if (rows.length === 0) {
    return (
      <p className="text-sm text-ink-soft" data-testid="case-notes-empty">
        Post a case note and you'll see it here.
      </p>
    );
  }

  const rowFor = (r: (typeof rows)[number]): ReactNode => {
    const { note } = r;
    const own = isOwn(note.authorUserId);
    return (
      <PrebillingRow
        key={note.noteId}
        layout={asCards ? 'card' : 'table'}
        note={note}
        bands={bands}
        allowedBands={allowedBands}
        bandLabels={bandLabels}
        officeSeat={false}
        columnCount={COLUMNS.length}
        currentRate={null}
        nativeFrom={null}
        readyProblem={readyProblem?.noteId === note.noteId ? readyProblem.message : null}
        editProblem={
          editProblem?.noteId === note.noteId ? { message: editProblem.message, date: editProblem.date } : null
        }
        viewerIsAuthor={own}
        viewerUserId={whoami?.user_id ?? null}
        canEditLine={canWrite && own}
        canMark={canWrite && own}
        isEditing={editingRow === note.noteId}
        returnFocus={returnFocusTo.current === note.noteId}
        onFocusReturned={onFocusReturned}
        onStartEdit={() => {
          setEditProblem(null);
          setRemoveProblem(null);
          setEditingRow(note.noteId);
        }}
        onCancel={() => {
          returnFocusTo.current = note.noteId;
          setEditProblem(null);
          setRemoveProblem(null);
          setEditingRow(null);
        }}
        onDateChanged={() =>
          setEditProblem((p) => (p === null || p.date === null ? p : { ...p, date: null }))
        }
        onSave={(patch, summary) => billingMut.mutate({ noteId: note.noteId, patch, summary })}
        onSaveLine={(summary) => lineMut.mutate({ noteId: note.noteId, summary })}
        readyPending={pendingReady[note.noteId]}
        onSetReady={(ready) => readyMut.mutate({ noteId: note.noteId, ready })}
        saving={(lineMut.isPending || billingMut.isPending) && editingRow === note.noteId}
        canRemove={canChangeNote(whoami, note.authorUserId)}
        onRemove={() => removeMut.mutate({ noteId: note.noteId })}
        removing={removeMut.isPending && removeMut.variables?.noteId === note.noteId}
        removeProblem={removeProblem?.noteId === note.noteId ? removeProblem.message : null}
        client={{
          name: r.clientName,
          href: r.clientInScope
            ? `/t/${tenantSlug}/clients/${r.clientId}?tab=notes#note-${note.noteId}`
            : null,
          inScope: r.clientInScope,
          demo: r.clientIsDemo,
          bandLabel: bandLabel(note.rateBand),
        }}
      />
    );
  };

  return (
    <>
      {showBulk && (
        <div className="mb-3 flex flex-wrap items-center justify-end gap-3">
          <Button
            variant="primary"
            size="sm"
            disabled={awaiting.length === 0 || Object.keys(pendingReady).length > 0}
            onClick={() => setConfirmMarking(awaiting.map((r) => r.note.noteId))}
            data-testid="my-prebilling-mark-all"
          >
            Mark my notes ready ({awaiting.length})
          </Button>
        </div>
      )}
      {confirmMarking !== null && (
        <ConfirmDialog
          title={markReadyTitle(confirmMarking.length)}
          body={MARK_READY_BODY}
          confirmLabel={`Mark ${confirmMarking.length} ready`}
          onConfirm={() => {
            const noteIds = confirmMarking;
            setConfirmMarking(null);
            for (const noteId of noteIds) readyMut.mutate({ noteId, ready: true });
          }}
          onCancel={() => setConfirmMarking(null)}
        />
      )}
      {asCards ? (
        <ul className="m-0 flex list-none flex-col gap-2.5 p-0" data-testid="my-prebilling-cards">
          {pageRows.map(rowFor)}
        </ul>
      ) : (
        // The wrapper is the table's scroll area and an inline-size
        // container: an open edit box is as wide as the visible area and
        // sticks to its left edge while the table scrolls sideways. The
        // minimum width is the five fixed columns (51rem) and the invoice
        // line's 24rem. It is positioned, so the Edit header's
        // screen-reader-only label is clipped with the table and never
        // widens the page.
        <div className="relative overflow-x-auto @container rounded-card border border-sage-200 bg-paper px-4 py-1 shadow-sh1 sm:px-5">
          <table className="w-full min-w-[75rem] table-fixed text-sm" data-testid="my-prebilling-table">
            <colgroup>
              {COLUMNS.map((c) => (
                <col key={c.name} className={c.width} />
              ))}
            </colgroup>
            <thead>
              <tr className="text-left text-label font-semibold uppercase tracking-label text-sage-400">
                {COLUMNS.map((c, i) => (
                  <th key={c.name} className={i === COLUMNS.length - 1 ? 'py-1.5 font-semibold' : 'py-1.5 pr-3 font-semibold'}>
                    {columnHeading(c.name)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-sage-800/[6%]">{pageRows.map(rowFor)}</tbody>
          </table>
        </div>
      )}
      <PaginationFooter
        offset={offset}
        pageSize={PAGE_SIZE}
        total={rows.length}
        onOffsetChange={(next) => setPager({ key: pagerKey, offset: next })}
        testIdPrefix="my-prebilling-page-"
      />
    </>
  );
}
